import {
  Inbox,
  UserX,
  AlertCircle,
  User,
  Star,
  Clock,
  CheckCircle,
  Archive,
  ChevronLeft,
  ChevronDown,
  X,
  Ticket,
  // Package,
  // BookOpen,
  BarChart3,
  FileBarChart,
  Shield,
  // LayoutDashboard,
  // FileText,
  Users,
  Kanban,
  FolderKanban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { queues } from "@sd/constants/ticketQueues";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { mapTicketsQueueToAdminPath, sd } from "@sd/lib/sdPaths";
import { AGENT_ALLOWED_TICKET_QUEUES } from "@sd/lib/sdAgentRouteAccess";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState } from "react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  "user-x": UserX,
  "alert-circle": AlertCircle,
  user: User,
  star: Star,
  clock: Clock,
  "check-circle": CheckCircle,
  archive: Archive,
  kanban: Kanban,
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeQueue: string;
  onQueueChange: (queueId: string) => void;
  activeTab: "tickets" | "assets";
  onTabChange: (tab: "tickets" | "assets") => void;
  /** Live counts per queue id — shown as badge next to each sidebar item */
  queueCounts?: Record<string, number>;
  /** Close mobile drawer after navigation */
  onNavigate?: () => void;
}

export function Sidebar({
  collapsed,
  onToggle,
  activeQueue,
  onQueueChange,
  activeTab,
  onTabChange,
  queueCounts = {},
  onNavigate,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isSuperAdmin, isSupportAgent, isEndUser } = useAuth();
  const isAgentOnly = role === "ServiceDesk_Agent";

  // Visibility per role
  // Super Admin   → Tickets, Reports, Admin (KB + Assets nav temp. off)
  // Support Agent → Tickets (KB + Assets nav temp. off)
  // End User      → My Portal (KB nav temp. off)
  const canSeeTickets = isSuperAdmin || isSupportAgent;
  // const canSeeAssets   = isSuperAdmin || isSupportAgent; // temp: Assets section commented out
  const canSeeReports = isSuperAdmin;
  const canSeeAdmin = isSuperAdmin;
  const canSeeMyPortal = isEndUser;

  const [ticketsOpen, setTicketsOpen] = useState(true);
  // const [assetsOpen, setAssetsOpen] = useState(false); // temp: Assets section commented out
  // const [kbOpen, setKbOpen] = useState(false); // temp: KB section commented out

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isProjectsActive = isActive(sd("projects"));
  const ticketsSectionActive = activeTab === "tickets" || isProjectsActive;

  const handleTicketNav = (queueId: string) => {
    onTabChange("tickets");
    onQueueChange(queueId);
    if (isSuperAdmin) {
      navigate(mapTicketsQueueToAdminPath(queueId));
      onNavigate?.();
      return;
    }
    navigate(sd(`tickets/${queueId}`));
    onNavigate?.();
  };

  const navAndClose = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const isDrawer = Boolean(onNavigate);

  // const handleAssetNav = (queueId: string) => {
  //   onTabChange('assets');
  //   onQueueChange(queueId);
  //   navigate(sd(`assets/${queueId}`));
  // };

  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r-2 border-sidebar-border bg-sidebar transition-all duration-300",
        collapsed ? "w-14" : "w-64"
      )}
    >
      {/* Mobile drawer — close button */}
      {isDrawer && (
        <div className="flex items-center justify-end border-b border-sidebar-border p-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate?.()}
            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}

      {/* Collapse Toggle — desktop only */}
      {!isDrawer && (
        <div className="flex items-center justify-end border-b border-sidebar-border p-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <ChevronLeft
              className={cn(
                "h-4 w-4 transition-transform",
                collapsed && "rotate-180"
              )}
            />
          </Button>
        </div>
      )}

      <div className="scrollbar-thin flex-1 overflow-y-auto py-2">
        {/* Tickets Section - Admin & Agent only */}
        {canSeeTickets && (
          <Collapsible
            open={!collapsed && ticketsOpen}
            onOpenChange={setTicketsOpen}
          >
            <CollapsibleTrigger asChild>
              <button
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-slate-100/50",
                  collapsed && "justify-center",
                  ticketsSectionActive
                    ? "rounded-r-lg border-l-4 border-l-slate-800 bg-slate-50 font-black text-slate-800"
                    : "text-sidebar-foreground"
                )}
                onClick={() => {
                  if (collapsed) {
                    handleTicketNav(isAgentOnly ? "project-status" : "all");
                  }
                }}
              >
                <Ticket className="text-slate-650 h-4 w-4 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left text-sm font-black uppercase tracking-wide text-slate-900">
                      Tickets
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-slate-500 transition-transform",
                        ticketsOpen && "rotate-180"
                      )}
                    />
                  </>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1.5 space-y-1 px-2">
              <button
                onClick={() => navAndClose(sd("tickets/project-status"))}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm leading-snug transition-all duration-150",
                  ticketsSectionActive && activeQueue === "project-status"
                    ? "border-slate-250/80 shadow-3xs bg-slate-100 font-semibold text-slate-900"
                    : "border-transparent font-medium text-sidebar-foreground hover:bg-sidebar-accent/40"
                )}
              >
                <BarChart3
                  className={cn(
                    "h-4 w-4 flex-shrink-0 transition-colors",
                    ticketsSectionActive && activeQueue === "project-status"
                      ? "text-slate-800"
                      : "text-sidebar-foreground/70"
                  )}
                />
                <span className="flex-1 truncate text-left">
                  Dashboard Project Status
                </span>
              </button>
              <button
                onClick={() =>
                  navAndClose(
                    isSuperAdmin ? sd("admin/report") : sd("tickets/report")
                  )
                }
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm leading-snug transition-all duration-150",
                  ticketsSectionActive && activeQueue === "report"
                    ? "border-slate-250/80 shadow-3xs bg-slate-100 font-semibold text-slate-900"
                    : "border-transparent font-medium text-sidebar-foreground hover:bg-sidebar-accent/40"
                )}
              >
                <FileBarChart
                  className={cn(
                    "h-4 w-4 flex-shrink-0 transition-colors",
                    ticketsSectionActive && activeQueue === "report"
                      ? "text-slate-800"
                      : "text-sidebar-foreground/70"
                  )}
                />
                <span className="flex-1 truncate text-left">Report</span>
              </button>
              {queues
                .filter(q =>
                  isAgentOnly
                    ? (AGENT_ALLOWED_TICKET_QUEUES as readonly string[]).includes(
                        q.id
                      )
                    : true
                )
                .map(queue => {
                  const Icon = iconMap[queue.icon] || Inbox;
                  const isQueueActive =
                    ticketsSectionActive && activeQueue === queue.id;
                  const isBreach = queue.id === "breaching";

                  return (
                    <button
                      key={queue.id}
                      onClick={() => handleTicketNav(queue.id)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm leading-snug transition-all duration-150",
                        isQueueActive
                          ? "border-slate-250/80 shadow-3xs bg-slate-100 font-semibold text-slate-900"
                          : "border-transparent font-medium text-sidebar-foreground hover:bg-sidebar-accent/40"
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-4 w-4 flex-shrink-0 transition-colors",
                          isQueueActive
                            ? "text-slate-800"
                            : "text-sidebar-foreground/70",
                          isBreach && "text-red-500"
                        )}
                      />
                      <span className="flex-1 truncate text-left">
                        {queue.label}
                      </span>
                    </button>
                  );
                })}
              <button
                onClick={() => navAndClose(sd("projects"))}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm leading-snug transition-all duration-150",
                  isProjectsActive
                    ? "border-slate-250/80 shadow-3xs bg-slate-100 font-semibold text-slate-900"
                    : "border-transparent font-medium text-sidebar-foreground hover:bg-sidebar-accent/40"
                )}
              >
                <FolderKanban
                  className={cn(
                    "h-4 w-4 flex-shrink-0 transition-colors",
                    isProjectsActive
                      ? "text-slate-800"
                      : "text-sidebar-foreground/70"
                  )}
                />
                <span className="flex-1 truncate text-left">Projects</span>
              </button>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Reports - Admin/Supervisor only */}
        {canSeeReports && (
          <button
            onClick={() => navAndClose(sd("reports"))}
            className={cn(
              "mt-1 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-xs transition-all duration-150",
              collapsed && "justify-center",
              isActive(sd("reports"))
                ? "border-slate-250/80 shadow-3xs bg-slate-100 font-bold text-slate-800"
                : "border-transparent font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent/40"
            )}
          >
            <BarChart3 className="text-slate-650 h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="font-bold">Reports</span>}
          </button>
        )}

        {/* Admin Panel - Super Admin only */}
        {isSuperAdmin && (
          <button
            onClick={() => navAndClose(sd("admin"))}
            className={cn(
              "mt-1 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-xs transition-all duration-150",
              collapsed && "justify-center",
              location.pathname.startsWith(sd("admin"))
                ? "border-slate-250/80 shadow-3xs bg-slate-100 font-bold text-slate-800"
                : "border-transparent font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent/40"
            )}
          >
            <Shield className="text-slate-650 h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="font-bold">Admin Panel</span>}
          </button>
        )}

        {/* My Tickets - End User shortcut */}
        {isEndUser && (
          <button
            onClick={() => navAndClose(sd("my-portal"))}
            className={cn(
              "mt-1 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-xs transition-all duration-150",
              collapsed && "justify-center",
              location.pathname.startsWith(sd("my-portal"))
                ? "border-slate-250/80 shadow-3xs bg-slate-100 font-bold text-slate-800"
                : "border-transparent font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent/40"
            )}
          >
            <Users className="text-slate-650 h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="font-bold">My Portal</span>}
          </button>
        )}

      </div>

    </aside>
  );
}
